import { Place } from './place';
import { TourClassTime } from './tourClassTime';

export type Tour = {
  id: string;
  name: string;
  description: string;
  tourNumber: string | null;
  abbreviation: string | null;
  studentNamesOfTour: string;
  amountOfStudentsInTour: number;
  escortIndividualCount: number;
  studentWithWheelchairCount: number;
  studentUtilityCounts: string | null;
  transportationCompanyId: string | null; // currently not used. TODO for: TICL-3579/filtering
  transportationCompanyName: string | null;
  vehicleId: string;
  driverId: string | null;
  busEscortId: string | null;
  validFrom: Date;
  validUntil: Date;
  stops: Stop[];
  studentIds: string[];
  schoolIds: string[];
  tourType: string;
  bundleId: string;
  plannedTour: boolean;
  duration: string;
  occupiedTime: string;
  occupiedDistance: string;
  overriddenOccupiedDistance: string;
  emptyDistancePercentage: string;
  days: string;
  targetDays: string;
  targetTime: string;
  createdAt: string;
  customAttributeId: string | null;
  tourReason: TourReason;
  tourPlanningAlgorithm: TourPlanningAlgorithm;
  plannedWithCustomTransportationPlace: boolean;
  busEscortInTour: boolean;
  overcrowded: boolean;
  shortestDistance: boolean;
  minutesAroundSchool: number;
  tourIsDeleted: boolean | false;
  tourDetailTime: TourDetailTime;
  tourDetailDistance: TourDetailDistanceInfo;
  tourDetailVehicle: TourDetailVehicle;
  tourAdaptionAction: TourAdaptationAction;
  tourCostAttributes: TourDetailCost | null;
  optimizeOrder: boolean;
};

export type Stop = {
  id: string;
  place: Place;
  studentsBoarding: string[];
  studentsDisembarking: string[];
  timeArrival: string;
  timeDeparture: string;
};

export type StopsOfDay = {
  day: string;
  stops: Stop[];
};

export type TourNumber = {
  id: string;
  number: number;
};

export type TourBundle = {
  id: string;
  name: string;
  description: string;
  tourIds: string[];
  bundleColor: string;
  tourClassTimes?: TourClassTime[];
};

export type TourSuitableAssignmentOptions = {
  availableVehicles: TourAttributeWrapper[];
  availableDrivers: TourAttributeNameWrapper[];
  availableBusEscorts: TourAttributeNameWrapper[];
  hasVirtualVehicle: boolean;
};

export type TourPlan = {
  id: string;
  name: string;
  tourIds: string[];
};

export enum TourFetchingType {
  PLANNED = 'PLANNED',
  REAL = 'REAL',
  ALL = 'ALL',
}
export enum TourReason {
  TRANSPORTATION_MODEL = 'TRANSPORTATION_MODEL',
  DEVIATION = 'DEVIATION',
  EXTRA_TOUR = 'EXTRA_TOUR',
  SPECIAL_TOUR = 'SPECIAL_TOUR',
  IGNORE_SCHEDULE = 'IGNORE_SCHEDULE',
}

export enum TourPlanningAlgorithm {
  SAME = 'SAME',
  SCHEDULE = 'SCHEDULE',
  SCHEDULE_AWARE = 'SCHEDULE_AWARE',
  IGNORE_SCHEDULE = 'IGNORE_SCHEDULE',
  EXTRA_TOUR = 'EXTRA_TOUR',
  SPECIAL_TOUR = 'SPECIAL_TOUR',
  GROUP_PRIORITY = 'GROUP_PRIORITY',
  NONE = 'NONE',
}

export enum TourAdaptationAction {
  ADD = 'SAME',
  REMOVE = 'REMOVE',
  REORDER = 'REORDER',
  REMOVE_LAST_AND_SHORTEN_TOUR = 'REMOVE_LAST_AND_SHORTEN_TOUR',
  REMOVE_LAST_AND_DELETE_TOUR = 'REMOVE_LAST_AND_DELETE_TOUR',
  NO_ACTION = 'NO_ACTION',
}

export function isPlanned(fetchingType: TourFetchingType) {
  return fetchingType === TourFetchingType.PLANNED;
}

export type PlanningReferenceType = 'STUDENTS' | 'TOURS' | 'SCHOOLS';

export type RoutingMethod = 'CURRENT' | 'SHORTEST_PATH' | 'SHORTEST_TIME';

export type DayHour = {
  day: string;
  hour: string | null;
};

export type TourAttributeWrapper = {
  id: string;
  name: string;
  value: string | null;
};

export type TourAttributeNameWrapper = {
  id: string;
  firstName: string;
  lastName: string;
  value: string | null;
};

export type TourDetail = {
  overviewInfo: TourDetailOverview;
  vehicleInfo: TourDetailVehicle;
  distanceInfo: TourDetailDistanceInfo;
  timeInfo: TourDetailTime;
  costInfo: DaySpecificTourDetailCost;
  studentIds: string[];
  deviations: TourDetailDeviation[];
  daysSchoolHours: string;
  targetDays: string;
  targetTime: string;
  changedAttributes: TourDetailChangedAttributes;
};

export type TourDetailOverview = {
  id: string;
  tempId: string;
  name: string | null;
  description: string | null;
  tourNumber: string | null;
  abbreviation: string | null;
  validFrom: string;
  validUntil: string;
  tourBundle: TourAttributeWrapper | null;
  tourType: string | null;
  tourPlan: TourAttributeWrapper | null;
  schools: TourAttributeWrapper[] | null;
  studentsCount: number | null;
  wheelchairCount: number | null;
  stopsCount: number | null;
  escortInBus: boolean;
  plannedWithCustomTransportationPlace: boolean;
  escortIndividualCount: number | null;
  utilities: StudentUtilitiesAndCount[] | null;
  routingMethod: string | null;
  createdAt: string;
  customAttributeId: string | null;
  tourReason: TourReason;
  overriddenOccupiedDistance: string | null;
  optimizeOrder: boolean;
};

export type StudentUtilitiesAndCount = {
  utility: string;
  count: number;
};

export type TourDetailVehicle = {
  vehicleIdName: TourAttributeWrapper;
  driverIdName: TourAttributeWrapper;
  escortIdName: TourAttributeWrapper;
  replacementDriverIdName: TourAttributeWrapper | null;
  freeSeats: number;
  freeWheelchairSeats: number;
  vehicleTypeName: string | null;
};

export type TourDetailDistanceInfo = {
  occupiedKilometerSum: string | null;
  emptyKilometerStart: string | null;
  emptyKilometerEnd: string | null;
  emptyKilometerSum: string | null;
  kilometerSum: string | null;
  placeNameEmptySegmentStart: string | null;
  placeNameEmptySegmentEnd: string | null;
};

export type TourDetailTime = {
  occupiedTime: string | null;
  emptySegmentsTimeStart: string | null;
  emptySegmentsTimeEnd: string | null;
  emptySegmentsTimeSum: string | null;
  boardingTimeSum: string | null;
  disembarkingTimeSum: string | null;
  waitingTimeSchool: string | null;
  timeBeforeSchoolStart: TourAttributeWrapper[] | null;
  timeAfterSchoolEnd: TourAttributeWrapper[] | null;
  tourTimeSum: string | null;
  additionDriverWorkingTime: string | null;
  driverWorkingTimeSum: string | null;
};

export type DaySpecificTourDetailCost = {
  costOccupiedKilometre: string | null;
  costEmptyKilometre: string | null;
  costFlatRate: string | null;
  costAddition: string | null;
  costOccupiedKilometreSum: string | null;
  costEmptyKilometreSum: string | null;
  costSum: string | null;
};

export type TourDetailCost = {
  id: string;
  costOccupiedKilometre: number | null;
  costEmptyKilometre: number | null;
  costFlatRate: number | null;
  costAddition: number | null;
  validFrom: Date;
  validUntil: Date;
};

export type TourCostForm = {
  costOccupiedKilometre?: number | null;
  costEmptyKilometre?: number | null;
  costFlatRate?: number | null;
  costAddition?: number | null;
  changesValidFrom: Date | null;
};

export type TourDetailDeviation = {
  id: string;
  date: string;
  studentId: string;
  tourId: string;
  type: 'MISSING' | 'ADDITIONAL';
};

export type TourDetailChangedAttributes = {
  driverChangedOverTime: boolean;
  escortChangedOverTime: boolean;
  vehicleChangedOverTime: boolean;
  costChangedOverTime: boolean;
  emptySectionChangedOverTime: boolean;
};

export type TourEditData = {
  id: string;
  name: string | null;
  description: string | null;
  tourNumber: number | 0;
  abbreviation: string | null;
  tourReason: TourReason;
  tourDays: number[];
  vehicle: TourAttributeWrapper;
  driver: TourAttributeWrapper;
  busEscort: TourAttributeWrapper;
  emptySection: TourDetailEmptySectionAssignment;
  validFrom: Date;
  validUntil: Date;
  bundleId: string | null;
  changesValidFrom: Date | null;
  plannedTour: boolean;
  cost: TourDetailCost;
  availableVehicles: TourAttributeWrapper[];
  availableDrivers: TourAttributeNameWrapper[];
  availableEscorts: TourAttributeNameWrapper[];
  costHistory: TourDetailCost[];
  vehicleAssignmentHistory: TourDetailVehicleAssignmentPeriod[];
  driverAssignmentHistory: TourDetailDriverAssignmentPeriod[];
  escortAssignmentHistory: TourDetailEscortAssignmentPeriod[];
  emptySectionAssignmentHistory: TourDetailEmptySectionAssignment[];
  customAttributeId: string | null;
  minutesAroundSchool: number | null;
  overriddenOccupiedDistance: string | null;
  tourType: string;
};

export type TourTimelineData = {
  id: string;
  name: string | null;
  tourNumber: number | 0;
  tourReason: TourReason;
  validFrom: Date;
  validUntil: Date;
  plannedTour: boolean;
  studentIds: string[];
  schoolIds: string[];
  tourType: string;
  occupiedTime: string;
  occupiedDistance: string;
  vehicleAssignmentHistory: TourDetailVehicleAssignmentPeriod[];
  driverAssignmentHistory: TourDetailDriverAssignmentPeriod[];
  escortAssignmentHistory: TourDetailEscortAssignmentPeriod[];
};

export enum TourAssignmentType {
  ESCORT = 'ESCORT',
  DRIVER = 'DRIVER',
  VEHICLE = 'VEHICLE',
  EMPTY_SECTION = 'EMPTY_SECTION',
}

export type TourDetailDriverAssignmentPeriod = {
  id: string;
  driver: TourAttributeWrapper;
  validFrom: string;
  validUntil: string;
};

export type TourDetailVehicleAssignmentPeriod = {
  id: string;
  vehicle: TourAttributeWrapper;
  validFrom: string;
  validUntil: string;
};

export type TourDetailEscortAssignmentPeriod = {
  id: string;
  employee: TourAttributeWrapper;
  validFrom: string;
  validUntil: string;
};

export type TourDetailEmptySectionAssignment = {
  id: string;
  toDeparturePlaceId: string;
  toDeparturePlaceName: string;
  fromArrivalPlaceId: string;
  fromArrivalPlaceName: string;
  validFrom: string;
  validUntil: string;
};

export type TourTransformationResult = {
  transformedTourIds: string;
  conflicts: TourTransformConflict[];
};

export type TourTransformConflict = {
  originalTourId: string;
  tourName: string;
  studentConflicts: StudentTourConflict[];
  tourPlanningExceptionError: string;
};

export type StudentTourConflict = {
  studentId: string;
  studentName: string;
  conflictingTours: ConflictingTour[];
  conflictingPlace: ConflictingPlace | null;
  conflictingTransportationModels: ConflictingTransportationModel[];
};

export type ConflictingTransportationModel = {
  transportationModelId: string;
  transportationModelName: string;
  validFrom: string;
  validUntil: string;
};

export type ConflictingTour = {
  tourId: string;
  tourName: string;
  tourDays: string[];
  conflictFrom: string;
  conflictTo: string;
};

export type ConflictingPlace = {
  eventPlaceAddress: string;
  tourPlaceAddress: string;
  eventPlaceId: string;
  tourPlaceId: string;
};

export type PartialTourData = {
  id: string;
  tourType: string;
  validFrom: string;
  validUntil: string;
};

export type TourBasicAttributesForm = {
  name?: string | null;
  description?: string | null;
  tourNumber?: string | null;
  abbreviation?: string | null;
  validFrom: Date | null;
  validUntil: Date | null;
  bundleId?: string | null;
  customAttributeId?: string | null;
  minutesAroundSchool?: number | null;
  overriddenOccupiedDistance?: string | null;
};

export type TourEscortAssignmentForm = {
  employeeId?: string | null;
  changesValidFrom: Date | null;
};

export type CustomTourFilterModel = {
  id: number;
  field: string;
  operator: string;
  value: string;
};

export type CustomTourFilterViewValue = 'MAP' | 'ALL';

export type TourPlanningData = {
  name: string | null;
  from: Date | null;
  until: Date | null;
  tourType: string;
  ids: string[];
  idType: string;
  algorithm: string;
  vehicleIds: string[];
  tourPlanningVehicleType: string;
  ignoreVehiclePlaces: boolean;
  virtualFleetVehicle: TourPlanningFleet | null;
  days: string[] | null;
  targetPlaceId: string | null;
  targetTime: string | null;
  considerCustomTransportationPlace: boolean;
  reserveSeatForBusEscort: boolean;
  shortestDistance: boolean;
};

export type TourPlanningFleet = {
  allSeats: number;
  wheelchairSeats: number;
};

export type TrackComparison = {
  id: string;
  trackId: string;
  tourId: string;
  employeeId: string;
  trackDate: string;
  createdAt: string;
  algorithm: string;
  durationDifferenceAbsolute: number | null;
  durationDifferenceRelative: number | null;
  distanceDifferenceAbsolute: number | null;
  distanceDifferenceRelative: number | null;
  otherResult: string | null;
};

export type ListDataIgnoreDate = {
  tour: boolean;
  showExtendedAttributes: boolean;
};

export enum TourTenderType {
  TOUR = 'TOUR',
  PLANNED_TOUR = 'PLANNED_TOUR',
  TOUR_BUNDLE = 'TOUR_BUNDLE',
}

export type TourTenderIdsAndType = {
  ids: string[];
  tenderType: TourTenderType;
};

export type TourTender = {
  tenderType: TourTenderType;
  selectedIds: string[];
  headerValidity: string;
  title: string;
  serviceDescription: string; // Leistungsbeschreibung
  showServiceDescription: boolean;
  showTourNumber: boolean;
  showStudentSchoolClass: boolean;
  showStudentYearOfBirth: boolean;
  showStudentUtility: boolean;
  showStudentMobility: boolean;
  showStudentShortNote: boolean;
  showStudentHouseNumber: boolean;
  showStudentHouseNumberInRange: boolean;
  showStudentName: boolean;
  offerDetailMatrix: TourTenderOfferMatrixElement[][]; // Angebotsangaben
  biddingFormDescription: string; // Hinweistext für Bieter
  showBiddingFormDescription: boolean;
  showBidderInformation: boolean;
  defaultTOArrivalTourTime: string;
  defaultFROMDepartureTourTime: string;
};

export type TourTenderOfferMatrixElement = {
  value: string;
};

export type InterkommunalerLastenausgleichConfig = {
  schoolIds: string[];
  stichtag: Date | null;
  faktorDefaultPercent: number | null;
  abzugDefaultEuro: number | null;
};

export type FromTourSuggestion = {
  id: string;
  toName: string;
  toStudents: number;
  toDays: string;
  toSchoolIds: string[];
  toTourType: string;
  status: string;
  fromName: string;
  fromStudents: number;
  fromDays: string;
};

export type TourStudentMatrixTour = {
  id: string;
  name: string;
  validFrom: Date;
  validUntil: Date;
  students: TourStudentMatrixStudent[];
  days: string;
};

export type TourStudentMatrixStudent = {
  id: string;
  givenName: string;
  sureName: string;
  wheelchair: boolean;
  escortInBus: boolean;
  escortIndividual: boolean;
  days: MatrixDay[];
};

export type MatrixDay = {
  time: string | null;
  tourId: string | null;
  schoolHourId: string | null;
  schoolHourGroup: string | null;
};

export type MatrixRow = {
  id: string;
  tourId: string;
  name: string;
  tourFrom: Date;
  tourUntil: Date;
  studentId: string;
  studentName: string;
  studentWheelchair: boolean;
  studentIndividualEscort: boolean;
  studentBusEscort: boolean;
  moFromTime: string | null;
  moFromTourId: string | null;
  moFromColor: string;
  tuFromTime: string | null;
  tuFromTourId: string | null;
  tuFromColor: string;
  weFromTime: string | null;
  weFromTourId: string | null;
  weFromColor: string;
  thFromTime: string | null;
  thFromTourId: string | null;
  thFromColor: string;
  frFromTime: string | null;
  frFromTourId: string | null;
  frFromColor: string;
};
